import React, { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Mic, Square, Sparkles, Volume2, Music, ChevronUp, ChevronDown, Check, Shuffle, ArrowUp, ArrowDown } from "lucide-react";
import { ElementLabel } from "./ElementLabel";
import IlluminatedBeing from "./IlluminatedBeing";
import { CHAKRAS } from "../data/chakras";
import { audioEngine } from "../lib/audioEngine";
import { useFocusStyle } from "../lib/focusScale";
import { useSavedState } from "../lib/savedState";
import { PitchEngine, freqToNote, NOTE_TO_PC } from "../lib/pitchEngine";

const HOLD_MS = 750; // how long the correct note must be held
const OCTAVES = [
  { id: "low", label: "Low", mult: 0.5 },
  { id: "mid", label: "Mid", mult: 1 },
  { id: "high", label: "High", mult: 2 },
];

export default function AttuneMode({ volume, focus, onEngage }) {
  const focusStyle = useFocusStyle(focus);
  const [droneOn, setDroneOn] = useState(false);
  const [octave, setOctave] = useSavedState("attune.octave", "mid");
  const [direction, setDirection] = useSavedState("attune.direction", "up");
  const descRef = useRef(false);
  const [seqActive, setSeqActive] = useState(false); // is the guided sequence currently running
  const [phase, setPhase] = useState("idle"); // idle | requesting | denied | demo | listen | complete
  const [roundLen, setRoundLen] = useState(1);
  const [activeIdx, setActiveIdx] = useState(-1);
  const [targetStep, setTargetStep] = useState(0); // current target chakra index (both modes)
  const [detected, setDetected] = useState(null); // { name, cents }
  const [holdPct, setHoldPct] = useState(0);
  const [geo, setGeo] = useState("seed");
  const [bloom, setBloom] = useState(false);
  const [pos, setPos] = useState(0); // position within the guided sequence
  const [flash, setFlash] = useState(null); // "Attuned ✓ Root" toast (free mode)
  const [guide, setGuide] = useState(null); // "up" | "down" | "in" | null

  const pitch = useRef(null);
  const micOn = useRef(false);
  const phaseRef = useRef("idle");
  const seqRef = useRef(false);
  const droneRef = useRef(false);
  const octaveRef = useRef(octave);
  const seqArrRef = useRef([]); // sequence of chakra indices (ascending or random)
  const posRef = useRef(0);
  const randomModeRef = useRef(false);
  const stepRef = useRef(0);
  const holdRef = useRef(0);
  const armedRef = useRef(true);
  const lastTs = useRef(0);
  const timers = useRef([]);

  useEffect(() => { phaseRef.current = phase; }, [phase]);
  useEffect(() => {
    octaveRef.current = octave;
    if (phaseRef.current === "listen" && droneRef.current) {
      audioEngine.drone(CHAKRAS[stepRef.current].frequency * octaveMult());
    }
  }, [octave]);

  const octaveMult = () => (OCTAVES.find((o) => o.id === octaveRef.current) || OCTAVES[1]).mult;
  const randNext = (avoid) => { let i; do { i = Math.floor(Math.random() * CHAKRAS.length); } while (i === avoid); return i; };
  const clearTimers = () => { timers.current.forEach(clearTimeout); timers.current = []; };
  const later = (fn, ms) => { const t = setTimeout(fn, ms); timers.current.push(t); return t; };

  const listening = phase === "listen";
  const targetChakra = listening ? CHAKRAS[targetStep] : null;
  const activeChakra = activeIdx >= 0 ? CHAKRAS[activeIdx] : null;
  const auraColor = (activeChakra || targetChakra)?.glow || "rgba(120,150,255,0.4)";
  const inTune = listening && detected && targetChakra && detected.name === targetChakra.note;
  const playing = phase === "demo" || phase === "listen" || phase === "complete";

  const ensureMic = async () => {
    if (micOn.current) return true;
    try {
      if (!pitch.current) pitch.current = new PitchEngine();
      await pitch.current.start(onPitch);
      micOn.current = true;
      return true;
    } catch (e) {
      setPhase("denied");
      return false;
    }
  };

  const setDrone = (idx) => {
    if (droneRef.current && idx >= 0) audioEngine.drone(CHAKRAS[idx].frequency * octaveMult());
    else audioEngine.stopDrone(0.3);
  };

  // ---------- SEQUENCE mode ----------
  const runDemo = () => {
    audioEngine.stopDrone(0.2);
    setPhase("demo");
    setBloom(false);
    setGeo("seed");
    setDetected(null);
    setHoldPct(0);
    const len = seqArrRef.current.length;
    // random mode speeds up (harder) as the sequence grows; ascending stays steady
    const toneMs = randomModeRef.current ? Math.max(300, 640 - (len - 1) * 45) : 600;
    const gap = randomModeRef.current ? Math.max(130, 260 - (len - 1) * 20) : 260;
    let k = 0;
    const step = () => {
      if (k >= seqArrRef.current.length) {
        setActiveIdx(-1);
        later(startListen, 500);
        return;
      }
      const ci = seqArrRef.current[k];
      setActiveIdx(ci);
      audioEngine.setVolume(volume);
      audioEngine.chime(CHAKRAS[ci].frequency * octaveMult());
      k += 1;
      later(() => { setActiveIdx(-1); later(step, gap); }, toneMs);
    };
    step();
  };

  const startListen = async () => {
    posRef.current = 0;
    setPos(0);
    const ci = seqArrRef.current[0];
    stepRef.current = ci;
    setTargetStep(ci);
    setActiveIdx(-1);
    holdRef.current = 0;
    armedRef.current = true;
    setHoldPct(0);
    lastTs.current = performance.now();
    setPhase("listen");
    const ok = await ensureMic();
    if (ok) setDrone(ci);
  };

  // ---------- shared pitch handler ----------
  const onPitch = (freq) => {
    if (phaseRef.current !== "listen") return;
    const now = performance.now();
    const dt = Math.min(120, now - lastTs.current);
    lastTs.current = now;

    const target = CHAKRAS[stepRef.current];
    if (!target) return;

    if (freq > 0) {
      const n = freqToNote(freq);
      setDetected({ name: n.name, cents: n.cents });
      // octave guide: signed semitone distance to the NEAREST occurrence of the target pitch class
      const midi = 69 + 12 * Math.log2(freq / 440);
      const targetPc = NOTE_TO_PC[target.note];
      const rp = ((Math.round(midi) % 12) + 12) % 12;
      let d = (((targetPc - rp + 6) % 12) + 12) % 12 - 6; // nearest pc offset, -6..5
      const dist = d - n.cents / 100;
      setGuide(Math.abs(dist) <= 0.5 ? "in" : dist > 0 ? "up" : "down");
      const match = n.pc === targetPc && Math.abs(n.cents) < 45;
      holdRef.current = match ? holdRef.current + dt : Math.max(0, holdRef.current - dt * 0.7);
    } else {
      setDetected(null);
      setGuide(null);
      holdRef.current = Math.max(0, holdRef.current - dt * 0.7);
    }

    if (holdRef.current <= 0) armedRef.current = true; // re-arm once released
    const pct = Math.min(1, holdRef.current / HOLD_MS);
    setHoldPct(pct);
    if (pct >= 1 && armedRef.current) onStepSuccess();
  };

  const onStepSuccess = () => {
    holdRef.current = 0;
    setHoldPct(0);
    const step = stepRef.current;
    audioEngine.setVolume(volume);
    audioEngine.chime(CHAKRAS[step].frequency * 2 * octaveMult());
    setBloom(true);
    later(() => setBloom(false), 900);

    if (!seqRef.current) {
      // free practice: stay on this chakra for continued attunement
      armedRef.current = false; // wait until note released before next success
      setFlash(`Attuned · ${CHAKRAS[step].english.replace(" Chakra", "")}`);
      later(() => setFlash(null), 1400);
      return;
    }

    const nextPos = posRef.current + 1;
    if (nextPos >= seqArrRef.current.length) {
      onRoundComplete();
    } else {
      posRef.current = nextPos;
      setPos(nextPos);
      const ci = seqArrRef.current[nextPos];
      stepRef.current = ci;
      setTargetStep(ci);
      setDrone(ci);
    }
  };

  const onRoundComplete = () => {
    audioEngine.stopDrone(0.3);
    setActiveIdx(-1);
    setGeo("flower");
    setBloom(true);
    const cap = randomModeRef.current ? 9 : CHAKRAS.length;
    if (seqArrRef.current.length >= cap) {
      setPhase("complete");
      audioEngine.chime(963);
      later(() => { setBloom(false); setGeo("seed"); }, 6500);
      return;
    }
    setPhase("demo");
    later(() => {
      const arr = seqArrRef.current;
      seqArrRef.current = randomModeRef.current
        ? [...arr, randNext(arr[arr.length - 1])]
        : [...arr, descRef.current ? CHAKRAS.length - 1 - arr.length : arr.length]; // next chakra along the channel
      setRoundLen(seqArrRef.current.length);
      setGeo("seed");
      setBloom(false);
      runDemo();
    }, 2200);
  };

  const startSequence = (random) => {
    onEngage?.();
    clearTimers();
    seqRef.current = true;
    setSeqActive(true);
    randomModeRef.current = random;
    descRef.current = !random && direction === "down";
    seqArrRef.current = [random ? Math.floor(Math.random() * CHAKRAS.length) : descRef.current ? CHAKRAS.length - 1 : 0];
    posRef.current = 0;
    setPos(0);
    setRoundLen(1);
    stepRef.current = seqArrRef.current[0];
    setTargetStep(seqArrRef.current[0]);
    setPhase("requesting");
    later(runDemo, 200);
  };
  const begin = () => startSequence(false);
  const beginRandom = () => startSequence(true);

  // ---------- free tuning: tap any chakra to hear + tune to it ----------
  const onChakraClick = async (c) => {
    const idx = CHAKRAS.findIndex((x) => x.id === c.id);
    // Tapping a chakra always plays its tone so you can hear it.
    audioEngine.setVolume(volume);
    audioEngine.chime(CHAKRAS[idx].frequency * octaveMult());

    // While the guided sequence is running, taps are preview-only (don't disrupt it).
    if (seqRef.current) return;

    // Otherwise: freely tune to this chakra.
    clearTimers();
    seqRef.current = false;
    setSeqActive(false);
    stepRef.current = idx;
    setTargetStep(idx);
    setActiveIdx(-1);
    setGeo("seed");
    setBloom(false);
    setFlash(null);
    holdRef.current = 0;
    armedRef.current = true;
    setHoldPct(0);
    lastTs.current = performance.now();
    setPhase("listen");
    const ok = await ensureMic();
    if (ok) setDrone(idx);
  };

  const stopMic = () => {
    if (pitch.current) pitch.current.stop();
    micOn.current = false;
  };

  const stopGame = () => {
    clearTimers();
    stopMic();
    audioEngine.stop(0.3);
    audioEngine.stopDrone(0.3);
    setPhase("idle");
    setActiveIdx(-1);
    setTargetStep(0);
    setDetected(null);
    setHoldPct(0);
    setGeo("seed");
    setBloom(false);
    setFlash(null);
    setGuide(null);
    seqRef.current = false;
    setSeqActive(false);
    seqArrRef.current = [];
    posRef.current = 0;
    setPos(0);
    randomModeRef.current = false;
    setRoundLen(1);
  };

  const toggleDrone = () => {
    const next = !droneOn;
    setDroneOn(next);
    droneRef.current = next;
    if (phaseRef.current === "listen") {
      if (next) audioEngine.drone(CHAKRAS[stepRef.current].frequency * octaveMult());
      else audioEngine.stopDrone(0.3);
    }
  };

  useEffect(() => () => {
    clearTimers();
    stopMic();
    audioEngine.stop(0.3);
    audioEngine.stopDrone(0.3);
  }, []);

  return (
    <div className="flex flex-1 flex-col items-center">
      <div className="relative -mb-6 mt-3 flex w-full flex-1 items-center justify-center sm:mb-0 sm:mt-0">
        <div className="relative" style={focusStyle}>
          <IlluminatedBeing
            activeChakraId={(activeChakra || targetChakra)?.id}
            onChakraClick={onChakraClick}
            breathScale={1}
            auraColor={auraColor}
            geometry={geo}
            bloom={bloom}
            afterglow={phase === "complete"}
            healingProgress={phase === "complete" ? 6 : -1}
          />
        </div>

        {/* Left cue: current instruction / target note */}
        <div
          className="absolute left-4 top-1/2 hidden -translate-y-1/2 text-left sm:block sm:left-12 lg:left-24"
          data-testid="attune-cues"
        >
          {phase === "demo" && (
            <p className="font-mono text-xs uppercase tracking-[0.3em] text-cyan-300/80" data-testid="attune-phase">
              Listen &amp; remember
            </p>
          )}
          {listening && targetChakra && (
            <>
              <ElementLabel chakra={targetChakra} testid="attune-element" className="mb-2" />
              <p className="font-mono text-xs uppercase tracking-[0.3em] text-cyan-300/80" data-testid="attune-phase">
                Sing the tone
              </p>
              <p className="mt-1 font-mono text-xs uppercase tracking-[0.3em] text-cyan-300/55">
                {targetChakra.english} · Note {targetChakra.note}
              </p>
              <p
                className="mt-4 font-serif text-6xl tracking-widest text-white/90 lg:text-7xl"
                style={{ color: targetChakra.color, textShadow: `0 0 30px ${targetChakra.glow}` }}
                data-testid="attune-target-note"
              >
                {targetChakra.note}
              </p>
              <div className="mt-2 flex items-center gap-1.5 font-mono text-xs uppercase tracking-[0.25em]" data-testid="attune-guide-cue">
                {guide === "up" && <span className="flex items-center gap-1 text-sky-300"><ChevronUp size={18} /> Higher</span>}
                {guide === "down" && <span className="flex items-center gap-1 text-amber-300"><ChevronDown size={18} /> Lower</span>}
                {guide === "in" && <span className="flex items-center gap-1 text-emerald-300"><Check size={16} /> {inTune ? "Hold" : "On note"}</span>}
              </div>
            </>
          )}
        </div>

        {/* free-mode attuned toast */}
        <AnimatePresence>
          {flash && (
            <motion.div
              key={flash}
              className="absolute bottom-6 left-1/2 -translate-x-1/2 rounded-full bg-emerald-500/20 px-4 py-1.5 font-mono text-xs uppercase tracking-[0.25em] text-emerald-200 backdrop-blur"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              data-testid="attune-flash"
            >
              {flash}
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* dock — in focus view only the live tuner / demo hint stays, floating at the bottom */}
      <div
        className={
          focus
            ? `fixed bottom-4 left-1/2 z-50 w-[min(92vw,36rem)] -translate-x-1/2 rounded-2xl border border-white/10 bg-slate-950/60 px-5 pt-4 backdrop-blur-xl ${listening || phase === "demo" ? "" : "hidden"}`
            : "relative flex w-full max-w-3xl flex-col rounded-2xl border border-white/10 bg-slate-950/70 p-4 backdrop-blur-xl sm:p-6"
        }
        data-testid="attune-dock"
      >
        {/* drone toggle + octave */}
        <div className={`order-last mt-3 flex-wrap items-center justify-center gap-2 sm:mt-5 sm:gap-3 ${focus ? "hidden" : "flex"}`}>
          <button
            data-testid="attune-drone-toggle"
            onClick={toggleDrone}
            className={`flex items-center gap-2 rounded-full border px-4 py-1.5 text-xs transition-colors ${droneOn ? "border-fuchsia-400/50 bg-fuchsia-500/20 text-fuchsia-100" : "border-white/10 bg-white/5 text-slate-400 hover:text-white"}`}
          >
            <Music size={13} /> Drone {droneOn ? "On" : "Off"}
          </button>
          <div className="flex items-center gap-1 rounded-full border border-white/10 bg-white/5 p-1">
            <span className="px-2 text-[10px] uppercase tracking-widest text-slate-500">Octave</span>
            {OCTAVES.map((o) => (
              <button
                key={o.id}
                data-testid={`attune-octave-${o.id}`}
                onClick={() => setOctave(o.id)}
                className={`rounded-full px-3 py-1.5 text-xs transition-colors ${octave === o.id ? "bg-white/15 text-white" : "text-slate-400 hover:text-white"}`}
              >
                {o.label}
              </button>
            ))}
          </div>
        </div>

        {phase === "idle" && !focus && (
          <p className="mb-3 sm:mb-4 text-center font-mono text-[11px] uppercase leading-relaxed tracking-[0.2em] text-slate-500" data-testid="attune-intro">
            Tap any chakra to hum along. Press Attune to climb root to crown, or choose Descend to travel crown to root.
          </p>
        )}

        {phase === "denied" && (
          <p className="mb-3 sm:mb-4 text-center font-mono text-xs uppercase tracking-[0.25em] text-rose-300" data-testid="attune-mic-denied">
            Microphone access is needed to attune your voice. Enable the mic and try again.
          </p>
        )}

        {/* live tuner */}
        {listening && (
          <div className="mb-3 sm:mb-5" data-testid="attune-tuner">
            {targetChakra && (
              <ElementLabel chakra={targetChakra} testid="attune-element-tuner" className="mb-1 text-[11px] sm:hidden" />
            )}
            <div className="mb-2 flex items-center justify-between font-mono text-[11px] uppercase tracking-[0.25em] text-slate-400">
              <span className="flex items-center gap-2">
                Sing
                <span
                  className="rounded-full px-2 py-0.5 text-xs font-semibold text-white"
                  style={{ background: targetChakra ? targetChakra.color : "#555", boxShadow: `0 0 12px ${targetChakra ? targetChakra.glow : "transparent"}` }}
                  data-testid="attune-target-chip"
                >
                  {targetChakra ? targetChakra.note : "—"}
                </span>
                <span className="hidden sm:inline text-slate-500">{targetChakra ? targetChakra.english.replace(" Chakra", "") : ""}</span>
              </span>
              <span>
                {seqActive ? `${randomModeRef.current ? "Random" : "Round"} ${roundLen} · Note ${pos + 1}/${roundLen}` : "Free practice"}
              </span>
            </div>
            <div className="mb-2 text-center font-mono text-[11px] uppercase tracking-[0.25em] text-slate-400">
              Your voice:{" "}
              <span className={inTune ? "text-emerald-300" : "text-slate-200"} data-testid="attune-detected-note">
                {detected ? detected.name : "—"}
              </span>
            </div>
            <div className="relative h-2 w-full overflow-hidden rounded-full bg-white/10">
              <div className="absolute left-1/2 top-0 h-full w-px -translate-x-1/2 bg-white/40" />
              {detected && (
                <div
                  className={`absolute top-1/2 h-3 w-3 -translate-x-1/2 -translate-y-1/2 rounded-full transition-all ${inTune ? "bg-emerald-400" : "bg-fuchsia-300"}`}
                  style={{ left: `${50 + Math.max(-50, Math.min(50, detected.cents))}%` }}
                />
              )}
            </div>
            <div className="mt-3 h-1.5 w-full overflow-hidden rounded-full bg-white/10">
              <div
                className="h-full rounded-full bg-gradient-to-r from-emerald-400 to-teal-300 transition-[width] duration-75"
                style={{ width: `${Math.round(holdPct * 100)}%` }}
                data-testid="attune-hold-progress"
              />
            </div>
            <p className="mt-2 flex items-center justify-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.3em] text-slate-400" data-testid="attune-guide">
              {guide === "up" && (
                <span className="flex items-center gap-1 text-sky-300"><ChevronUp size={14} /> Sing higher</span>
              )}
              {guide === "down" && (
                <span className="flex items-center gap-1 text-amber-300"><ChevronDown size={14} /> Sing lower</span>
              )}
              {guide === "in" && (
                <span className="flex items-center gap-1 text-emerald-300"><Check size={13} /> {inTune ? "Hold it…" : "On the note"}</span>
              )}
              {!guide && <span className="text-slate-500">Slide your pitch onto the note</span>}
            </p>
          </div>
        )}

        {phase === "demo" && (
          <p className="mb-3 sm:mb-5 flex items-center justify-center gap-2 text-center font-mono text-[11px] uppercase tracking-[0.3em] text-cyan-300/80" data-testid="attune-demo-hint">
            <Volume2 size={14} /> The being is singing the sequence…
          </p>
        )}

        <div className={`mb-2 flex-col items-center justify-center gap-2 ${focus ? "hidden" : "flex"}`}>
          {playing ? (
            <button
              data-testid="attune-stop"
              onClick={stopGame}
              className="flex items-center gap-2 rounded-full bg-white/10 px-6 py-2.5 text-sm font-medium text-white transition-transform hover:scale-105"
            >
              <Square size={15} /> Stop
            </button>
          ) : (
            <>
              <div className="flex flex-wrap items-center justify-center gap-2 sm:gap-3">
                <div className="order-last flex gap-1 rounded-full border border-white/10 bg-white/5 p-1 sm:order-none sm:justify-self-end">
                  {[
                    { id: "up", label: "Ascend", Icon: ArrowUp },
                    { id: "down", label: "Descend", Icon: ArrowDown },
                  ].map(({ id, label, Icon }) => (
                    <button
                      key={id}
                      data-testid={`attune-direction-${id}`}
                      onClick={() => setDirection(id)}
                      className={`flex items-center gap-1 rounded-full px-3 py-1.5 text-xs transition-colors ${direction === id ? "bg-white/15 text-white" : "text-slate-400 hover:text-white"}`}
                    >
                      <Icon size={12} /> {label}
                    </button>
                  ))}
                </div>
                <div className="flex items-center gap-2">
                <button
                  data-testid="attune-start"
                  onClick={begin}
                  className="flex items-center gap-2 rounded-full bg-gradient-to-r from-emerald-500 to-teal-400 px-6 py-2.5 text-sm font-medium text-white shadow-lg shadow-emerald-500/30 transition-transform hover:scale-105"
                >
                  <Mic size={16} /> Attune
                </button>
                <button
                  data-testid="attune-random"
                  onClick={beginRandom}
                  className="flex items-center gap-2 rounded-full border border-fuchsia-400/40 bg-white/5 px-6 py-2.5 text-sm font-medium text-fuchsia-100 transition-transform hover:scale-105 hover:bg-white/10"
                >
                  <Shuffle size={16} /> Random
                </button>
                </div>
              </div>
              {phase === "idle" && !focus && (
                <p className="mt-1 text-center font-mono text-[11px] uppercase leading-relaxed tracking-[0.2em] text-slate-500" data-testid="attune-intro-more">
                  Or press Random to jump between chakras and get harder as it grows — hum each note back (any octave) and hold it steady.
                </p>
              )}
            </>
          )}
        </div>
      </div>

      {/* completion overlay (sequence) */}
      <AnimatePresence>
        {phase === "complete" && (
          <motion.div
            className="fixed inset-0 z-40 flex flex-col items-center justify-center text-center"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={stopGame}
            data-testid="attune-complete"
          >
            <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" />
            <motion.div
              className="relative z-10 flex flex-col items-center gap-3 px-6"
              initial={{ y: 16, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.3, duration: 0.9 }}
            >
              <Sparkles className="text-amber-200" size={30} />
              <p className="font-serif text-4xl text-white drop-shadow-[0_0_25px_rgba(255,255,255,0.4)]">
                Fully Attuned
              </p>
              <p className="max-w-md font-serif text-lg italic text-slate-200">
                "Your voice has awakened all seven centers. You are in tune with the light."
              </p>
              <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-cyan-300/70">tap to finish</p>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
